import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { GetEmailCyclesRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailCyclesRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-types";
import { LeoRPCResult } from "@surya-digital/leo-ts-runtime";
import { useGetEmailCyclesRPCClientImpl } from "../rpcs/RPC";
import {
  createEmailCycleModel,
  EmailCycleModel,
} from "../models/EmailCycleModel";
import { createDeliveryStatsModel } from "../models/DeliveryStatsModel";
import { createBoundedNameModel } from "../../models/BoundedNameModel";
import { TypesSortOrder } from "@pulse/pulse-rpcs/lib/types/typesSortOrder";
import {
  createStopEmailCycleStore,
  StopEmailCycleStore,
} from "./StopEmailCycleStore";
import {
  createDeliveryStatsForProjectStore,
  DeliveryStatsForProjectStore,
} from "./DeliveryStatsForProjectStore";
import {
  createDownloadStatsForProjectStore,
  DownloadStatsForProjectStore,
} from "./DownloadStatsForProjectStore";

export const EMAIL_CYCLES_PER_PAGE = 10;

const getInitialState = (): SnapshotIn<typeof EmailCyclesStore> => ({
  emailCycles: [],
  totalItems: 0,
  loadingEmailCycles: false,
  getEmailCyclesRPCError: null,
  stopEmailCycleStore: createStopEmailCycleStore(),
  deliveryStatsForProjectStore: createDeliveryStatsForProjectStore(),
  downloadStatsForProjectStore: createDownloadStatsForProjectStore(),
});

export const EmailCyclesStore = types
  .model("GetEmailCyclesStore", {
    emailCycles: types.array(EmailCycleModel),
    totalItems: types.integer,
    loadingEmailCycles: types.boolean,
    getEmailCyclesRPCError: types.maybeNull(
      types.enumeration(
        "GetEmailCyclesRPCError",
        Object.values(GetEmailCyclesRPC.RPCError),
      ),
    ),
    stopEmailCycleStore: StopEmailCycleStore,
    deliveryStatsForProjectStore: DeliveryStatsForProjectStore,
    downloadStatsForProjectStore: DownloadStatsForProjectStore,
  })
  .actions((store) => ({
    getEmailCycles: flow(function* (
      projectId: string,
      pageIndex = 1,
      sortOrder = TypesSortOrder.TypesSortOrder.DESCENDING,
      sortColumn = GetEmailCyclesRPC.RequestEnums.SortColumn.SortColumn
        .MODIFIED_AT,
      loadingOptionsChange = false,
    ) {
      if (!loadingOptionsChange) store.loadingEmailCycles = true;
      store.getEmailCyclesRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const request = new GetEmailCyclesRPC.Request(
          new LeoUUID(projectId),
          pageIndex - 1,
          EMAIL_CYCLES_PER_PAGE,
          sortOrder,
          sortColumn,
        );

        const result: LeoRPCResult<
          GetEmailCyclesRPC.Response,
          GetEmailCyclesRPC.Errors.Errors
        > = yield useGetEmailCyclesRPCClientImpl(apiClient).execute(request);

        if ("response" in result) {
          const emailCycles = result.response.emailCycles.map((cycle) =>
            createEmailCycleModel({
              id: cycle.id.uuid,
              cycleName: createBoundedNameModel({
                name: cycle.cycleName.name,
              }),
              createdBy: cycle.createdBy,
              createdAt: cycle.createdAt.timestamp,
              modifiedAt: cycle.modifiedAt.timestamp,
              scheduledAt: cycle.scheduledAt.timestamp,
              deliveryStats: createDeliveryStatsModel({
                total: cycle.deliveryStats.total,
                queued: cycle.deliveryStats.queued,
                sent: cycle.deliveryStats.sent,
                delivered: cycle.deliveryStats.delivered,
                viewed: cycle.deliveryStats.viewed,
                unsent: cycle.deliveryStats.unsent,
                optOut: cycle.deliveryStats.optOut,
                bounces: cycle.deliveryStats.bounces,
                complaints: cycle.deliveryStats.complaints,
              }),
              status: cycle.status,
            }),
          );
          store.emailCycles.replace(emailCycles);
          store.totalItems = result.response.totalItems;
          return;
        }

        switch (result.error.code) {
          case GetEmailCyclesRPC.RPCError.InvalidProjectId:
          case GetEmailCyclesRPC.RPCError.InvalidPageIndex:
            store.getEmailCyclesRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        if (!loadingOptionsChange) store.loadingEmailCycles = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createEmailCyclesStore = (): Instance<typeof EmailCyclesStore> =>
  EmailCyclesStore.create(getInitialState());
